import { useState, useEffect, useRef } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";

export interface MaintenanceCosts {
  // OPEX from key-figures
  opexCosts: number | null;
  capexCosts: number | null;
  invoicedRentLines: number | null;
  opexPercentage: number | null;

  // Energy costs from ledger accounts
  heatingCosts: number | null; // 612010
  electricityCosts: number | null; // 612020
  waterCosts: number | null; // 612030
  totalEnergyCosts: number | null;
}

interface UseMaintenanceCostsReturn {
  costs: MaintenanceCosts;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// Cache to prevent re-fetches
let cachedCosts: MaintenanceCosts | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useMaintenanceCosts = (): UseMaintenanceCostsReturn => {
  const { session } = useSession();
  const [costs, setCosts] = useState<MaintenanceCosts>(
    cachedCosts || {
      opexCosts: null,
      capexCosts: null,
      invoicedRentLines: null,
      opexPercentage: null,
      heatingCosts: null,
      electricityCosts: null,
      waterCosts: null,
      totalEnergyCosts: null,
    }
  );
  const [isLoading, setIsLoading] = useState(!cachedCosts);
  const [error, setError] = useState<string | null>(null);
  const fetchInProgressRef = useRef(false);

  const fetchCosts = async (force = false) => {
    if (!session) return;

    // Check cache
    if (
      !force &&
      cachedCosts &&
      cacheTimestamp &&
      Date.now() - cacheTimestamp < CACHE_DURATION
    ) {
      setCosts(cachedCosts);
      setIsLoading(false);
      return;
    }

    if (fetchInProgressRef.current) return;

    try {
      fetchInProgressRef.current = true;
      setIsLoading(true);
      setError(null);

      // Fetch key-figures for OPEX
      const keyFiguresResponse = await fetchWithAuth(
        `${API_URL}/key-figures`,
        { method: "GET" },
        session
      );

      let opexCosts: number | null = null;
      let capexCosts: number | null = null;
      let invoicedRentLines: number | null = null;

      if (keyFiguresResponse.ok) {
        const { data } = await keyFiguresResponse.json();
        opexCosts = data?.nonChargeableOpexCosts ?? null;
        capexCosts = data?.capexCosts ?? null;
        invoicedRentLines = data?.invoicedRentLines ?? null;
      }

      // Calculate OPEX percentage
      let opexPercentage: number | null = null;
      if (opexCosts !== null && invoicedRentLines !== null && invoicedRentLines !== 0) {
        opexPercentage = Math.abs((opexCosts / invoicedRentLines) * 100);
      }

      // Fetch energy costs from specific ledger accounts
      // Using current year date range
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      let heatingCosts: number | null = null;
      let electricityCosts: number | null = null;
      let waterCosts: number | null = null;

      try {
        // Fetch energy costs from ledger accounts endpoint
        const energyResponse = await fetchWithAuth(
          `${API_URL}/key-figures/energy-costs?startDate=${startDate}&endDate=${endDate}`,
          { method: "GET" },
          session
        );

        if (energyResponse.ok) {
          const energyData = await energyResponse.json();
          heatingCosts = energyData?.heatingCosts ?? null; // 612010
          electricityCosts = energyData?.electricityCosts ?? null; // 612020
          waterCosts = energyData?.waterCosts ?? null; // 612030
        }
      } catch (energyError) {
        console.error("Error fetching energy costs:", energyError);
        // Continue without energy costs - they will show as null
      }

      // Calculate total energy costs
      const totalEnergyCosts =
        (heatingCosts || 0) + (electricityCosts || 0) + (waterCosts || 0) || null;

      const newCosts: MaintenanceCosts = {
        opexCosts,
        capexCosts,
        invoicedRentLines,
        opexPercentage,
        heatingCosts,
        electricityCosts,
        waterCosts,
        totalEnergyCosts,
      };

      cachedCosts = newCosts;
      cacheTimestamp = Date.now();

      setCosts(newCosts);
      setIsLoading(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Fout bij ophalen kosten";
      setError(errorMessage);
      setIsLoading(false);
      console.error("Error fetching maintenance costs:", err);
    } finally {
      fetchInProgressRef.current = false;
    }
  };

  const refresh = async () => {
    cachedCosts = null;
    cacheTimestamp = null;
    await fetchCosts(true);
  };

  useEffect(() => {
    fetchCosts();
  }, [session]);

  return {
    costs,
    isLoading,
    error,
    refresh,
  };
};
